// 排行榜页：本班 / 全站 双 Tab + 30 天 / 全部历史 双窗口 + 三种指标
// - 顶部三个切换：scope (class/global) + window (30d/all) + metric (avg/drill_count/s_count)
// - 表格：rank / display_name / class_name (global时) / total_drills / avg_score / s_count
// - 高亮"我"的所在行
// - 若我不在 top50，额外用单独一行显示
// - 游客模式：mock 数据，按 scope/window/metric 动态排序

import { useEffect, useState } from 'react';
import { leaderboardApi, type LeaderboardEntry } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { usePageStore } from '@/stores/pageStore';
import { ArrowLeft, Trophy, RefreshCw } from 'lucide-react';

const SCOPE_OPTIONS: Array<{ value: 'class' | 'global'; label: string }> = [
  { value: 'class', label: '本班' },
  { value: 'global', label: '全站' },
];

const WINDOW_OPTIONS: Array<{ value: '30d' | 'all'; label: string }> = [
  { value: '30d', label: '近 30 天' },
  { value: 'all', label: '全部历史' },
];

const METRIC_OPTIONS: Array<{ value: 'avg' | 'drill_count' | 's_count'; label: string }> = [
  { value: 'avg',         label: '平均分' },
  { value: 'drill_count', label: '演练数' },
  { value: 's_count',     label: 'S 级数' },
];

const RANK_BADGE: Record<number, string> = {
  1: 'bg-yellow-400/20 text-yellow-400 border-yellow-400/50',
  2: 'bg-slate-300/20 text-slate-300 border-slate-300/50',
  3: 'bg-orange-400/20 text-orange-400 border-orange-400/50',
};

// =============================================================
// 游客模式 mock 排行榜数据
// =============================================================
// 18 名学员（含两个班级），每个学员有完整的统计数据
// 游客"我"作为 guest-stu，排名会根据 metric 变化
interface MockStudent {
  user_id: string;
  display_name: string;
  class_id: string;
  class_name: string;
  // 近30天数据
  drills_30d: number;
  avg_30d: number;
  s_30d: number;
  // 全部历史数据
  drills_all: number;
  avg_all: number;
  s_all: number;
}

const MOCK_STUDENTS: MockStudent[] = [
  { user_id: 'ms1',  display_name: '陆嘉懿', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 24, avg_30d: 92.5, s_30d: 8,  drills_all: 48, avg_all: 90.1, s_all: 14 },
  { user_id: 'ms2',  display_name: '秦书瑶', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 22, avg_30d: 90.3, s_30d: 7,  drills_all: 41, avg_all: 88.7, s_all: 12 },
  { user_id: 'ms3',  display_name: '韩沐辰', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 26, avg_30d: 89.8, s_30d: 6,  drills_all: 52, avg_all: 87.5, s_all: 11 },
  { user_id: 'ms4',  display_name: '沈语桐', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 20, avg_30d: 88.6, s_30d: 5,  drills_all: 38, avg_all: 86.2, s_all: 9 },
  { user_id: 'ms5',  display_name: '江昱珩', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 28, avg_30d: 87.4, s_30d: 6,  drills_all: 55, avg_all: 85.9, s_all: 10 },
  { user_id: 'ms6',  display_name: '叶芷萱', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 19, avg_30d: 86.1, s_30d: 4,  drills_all: 35, avg_all: 84.8, s_all: 8 },
  { user_id: 'ms7',  display_name: '林晓彤', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 21, avg_30d: 85.3, s_30d: 4,  drills_all: 40, avg_all: 83.6, s_all: 7 },
  { user_id: 'ms8',  display_name: '陈思远', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 18, avg_30d: 83.9, s_30d: 3,  drills_all: 33, avg_all: 82.4, s_all: 6 },
  { user_id: 'ms9',  display_name: '周子涵', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 17, avg_30d: 82.7, s_30d: 3,  drills_all: 31, avg_all: 81.2, s_all: 5 },
  { user_id: 'ms10', display_name: '赵明轩', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 16, avg_30d: 81.5, s_30d: 2,  drills_all: 29, avg_all: 80.3, s_all: 4 },
  { user_id: 'ms11', display_name: '徐若曦', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 23, avg_30d: 80.8, s_30d: 3,  drills_all: 44, avg_all: 79.6, s_all: 6 },
  { user_id: 'ms12', display_name: '黄雨萱', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 15, avg_30d: 79.4, s_30d: 2,  drills_all: 27, avg_all: 78.5, s_all: 3 },
  { user_id: 'ms13', display_name: '孙鹤轩', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 14, avg_30d: 78.2, s_30d: 2,  drills_all: 26, avg_all: 77.1, s_all: 3 },
  { user_id: 'ms14', display_name: '马清妍', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 13, avg_30d: 76.9, s_30d: 1,  drills_all: 24, avg_all: 75.8, s_all: 2 },
  { user_id: 'ms15', display_name: '高子睿', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 12, avg_30d: 75.3, s_30d: 1,  drills_all: 22, avg_all: 74.2, s_all: 2 },
  { user_id: 'ms16', display_name: '郑思琪', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 11, avg_30d: 73.8, s_30d: 1,  drills_all: 20, avg_all: 72.6, s_all: 1 },
  { user_id: 'ms17', display_name: '吴宇泽', class_id: 'demo-1', class_name: '化工工艺实训班',
    drills_30d: 10, avg_30d: 71.5, s_30d: 0,  drills_all: 18, avg_all: 70.3, s_all: 1 },
  { user_id: 'ms18', display_name: '杨梦洁', class_id: 'demo-2', class_name: '智能制造实训班',
    drills_30d: 9,  avg_30d: 69.2, s_30d: 0,  drills_all: 16, avg_all: 68.7, s_all: 0 },
];

// 游客"我"的数据 — 排在中间偏上
const GUEST_ME: MockStudent = {
  user_id: 'guest', display_name: '游客·我', class_id: 'demo-1', class_name: '化工工艺实训班',
  drills_30d: 16, avg_30d: 84.2, s_30d: 3,
  drills_all: 30, avg_all: 83.1, s_all: 5,
};

/** 根据当前 scope/window/metric 生成游客排行榜 */
function buildGuestLeaderboard(
  scope: 'class' | 'global',
  window_: '30d' | 'all',
  metric: 'avg' | 'drill_count' | 's_count',
): { leaderboard: LeaderboardEntry[]; my_rank: number; my_extra_row: LeaderboardEntry | null; total: number; note?: string } {
  // scope=class 时只显示 demo-1 班；global 时显示全部
  const pool = scope === 'class'
    ? MOCK_STUDENTS.filter((s) => s.class_id === 'demo-1')
    : MOCK_STUDENTS;
  // 包含"我"
  const all = [...pool, GUEST_ME];

  const getMetric = (s: MockStudent): number => {
    if (metric === 'avg') return window_ === '30d' ? s.avg_30d : s.avg_all;
    if (metric === 'drill_count') return window_ === '30d' ? s.drills_30d : s.drills_all;
    return window_ === '30d' ? s.s_30d : s.s_all;
  };
  const getDrills = (s: MockStudent) => window_ === '30d' ? s.drills_30d : s.drills_all;
  const getAvg = (s: MockStudent) => window_ === '30d' ? s.avg_30d : s.avg_all;
  const getSCount = (s: MockStudent) => window_ === '30d' ? s.s_30d : s.s_all;

  // 按 metric 降序排序
  const sorted = [...all].sort((a, b) => getMetric(b) - getMetric(a));

  const leaderboard: LeaderboardEntry[] = sorted.map((s, i) => ({
    rank: i + 1,
    user_id: s.user_id,
    display_name: s.display_name,
    class_id: s.class_id,
    class_name: s.class_name,
    total_drills: getDrills(s),
    avg_score: getAvg(s),
    s_count: getSCount(s),
  }));

  const myIdx = leaderboard.findIndex((e) => e.user_id === 'guest');
  const my_rank = myIdx + 1;
  // "我"在 top50 内就不需要额外行
  const my_extra_row = my_rank > 50 ? leaderboard[myIdx] : null;
  // top50 内的列表
  const topList = leaderboard.slice(0, 50);

  return { leaderboard: topList, my_rank, my_extra_row, total: leaderboard.length };
}

export const LeaderboardPage: React.FC = () => {
  const setPage = usePageStore((s) => s.setPage);
  const user = useAuthStore((s) => s.user);
  const isGuest = user?.id === 'guest';
  const [scope, setScope]   = useState<'class' | 'global'>('class');
  const [window_, setWindow] = useState<'30d' | 'all'>('30d');
  const [metric, setMetric] = useState<'avg' | 'drill_count' | 's_count'>('avg');
  const [data, setData] = useState<{
    leaderboard: LeaderboardEntry[];
    my_rank: number | null;
    my_extra_row: LeaderboardEntry | null;
    total: number;
    note?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (isGuest) {
        const r = buildGuestLeaderboard(scope, window_, metric);
        setData(r);
        return;
      }
      const r = await leaderboardApi.query({ scope, window: window_, metric });
      setData(r);
    } catch {
      // 静默
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, window_, metric]);

  return (
    <div className="h-full overflow-y-auto bg-bg-primary text-text-primary">
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        {/* 顶部 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage(isGuest && user?.role === 'teacher' ? 'dashboard' : 'gallery')}
              className="p-2 -ml-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-bg-tertiary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                排行榜
                {isGuest && <span className="text-xs px-2 py-0.5 bg-cyan-500/15 text-cyan-400 rounded-full">游客演示</span>}
              </h1>
              <p className="text-xs text-text-muted mt-0.5">看看你在 {scope === 'class' ? '班级' : '全站'} 中的位置</p>
            </div>
          </div>
          <button
            onClick={() => void load()}
            className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary bg-bg-secondary border border-border rounded-lg flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            刷新
          </button>
        </div>

        {/* 三组 Tab */}
        <div className="bg-bg-secondary border border-border rounded-lg p-3 flex flex-wrap gap-3">
          <TabGroup label="范围" options={SCOPE_OPTIONS} value={scope} onChange={(v) => setScope(v as any)} />
          <TabGroup label="时间" options={WINDOW_OPTIONS} value={window_} onChange={(v) => setWindow(v as any)} />
          <TabGroup label="指标" options={METRIC_OPTIONS} value={metric} onChange={(v) => setMetric(v as any)} />
        </div>

        {/* 我的排名提示 */}
        {data && (
          <div className="bg-primary/10 border border-primary/40 rounded-lg p-3 text-sm flex items-center justify-between">
            <span>
              {data.my_rank ? (
                <>
                  当前你的排名：<strong className="text-primary text-base">#{data.my_rank}</strong>
                  <span className="ml-2 text-text-muted">/ 共 {data.total} 人</span>
                </>
              ) : (
                <span className="text-text-muted">你还没有演练记录</span>
              )}
            </span>
          </div>
        )}

        {/* 表格 */}
        <div className="bg-bg-secondary border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-10 text-center text-sm text-text-muted">加载中...</div>
          ) : !data || data.leaderboard.length === 0 ? (
            <div className="py-10 text-center text-sm text-text-muted">
              {data?.note ? data.note : '尚无榜单数据'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bg-tertiary text-xs text-text-muted">
                <tr>
                  <th className="px-3 py-2 w-16 text-center">名次</th>
                  <th className="px-3 py-2 text-left">学员</th>
                  {scope === 'global' && <th className="px-3 py-2 text-left">班级</th>}
                  <th className="px-3 py-2 text-right">演练数</th>
                  <th className="px-3 py-2 text-right">平均分</th>
                  <th className="px-3 py-2 text-right">S 级数</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((row) => (
                  <Row key={row.user_id} row={row} isMe={row.user_id === user?.id || (isGuest && row.user_id === 'guest')} showClass={scope === 'global'} />
                ))}
                {data.my_extra_row && (
                  <>
                    <tr className="border-t border-border">
                      <td colSpan={scope === 'global' ? 6 : 5} className="px-3 py-1 text-center text-xs text-text-muted">
                        … 你的位置 …
                      </td>
                    </tr>
                    <Row row={data.my_extra_row} isMe={true} showClass={scope === 'global'} />
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const TabGroup: React.FC<{
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, options, value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-text-muted">{label}</span>
    <div className="flex bg-bg-tertiary rounded p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-xs rounded transition-colors ${
            value === o.value ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const Row: React.FC<{ row: LeaderboardEntry; isMe: boolean; showClass: boolean }> = ({ row, isMe, showClass }) => (
  <tr className={`border-t border-border ${isMe ? 'bg-primary/10' : ''}`}>
    <td className="px-3 py-2.5 text-center">
      {RANK_BADGE[row.rank] ? (
        <span className={`inline-block min-w-[28px] px-1.5 py-0.5 rounded font-bold border ${RANK_BADGE[row.rank]}`}>
          #{row.rank}
        </span>
      ) : (
        <span className="text-text-secondary font-mono">#{row.rank}</span>
      )}
    </td>
    <td className="px-3 py-2.5">
      <span className={`font-medium ${isMe ? 'text-primary' : 'text-text-primary'}`}>{row.display_name}</span>
      {isMe && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-primary text-white rounded-full">你</span>}
    </td>
    {showClass && (
      <td className="px-3 py-2.5 text-xs text-text-muted">{row.class_name ?? '-'}</td>
    )}
    <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{row.total_drills}</td>
    <td className="px-3 py-2.5 text-right font-mono text-text-primary">{row.avg_score.toFixed(1)}</td>
    <td className="px-3 py-2.5 text-right font-mono text-yellow-400">{row.s_count}</td>
  </tr>
);
